/* screens/utm/UtmCapture.jsx — фолбэк-роут перехода по метке
   (/r/:code, /r/:code/:to, /r/:code/buy/:slug, /r/:code/course/:slug).

   Боевой путь перехода — серверный `GET /r/<code>`: он ставит куку устройства и
   сам редиректит куда надо. Но Django обслуживает только /api/, /admin/,
   /static/ и /media/, поэтому серверный путь жив ровно до тех пор, пока в nginx
   стоит `location ^~ /r/`. Снипеты nginx у нас ставятся руками и уже терялись —
   без этого экрана переход тогда молча уезжал бы в SPA-fallback и не считался
   вовсе. Здесь мы добираем его POST'ом /api/utm/hit/ и уводим дальше.

   КУДА уводить, решает БЭКЕНД (`url` в ответе): у метки несколько назначений
   (регистрация, лендинг, демо-стенд, покупка тарифа, страница курса), адрес
   лендинга живёт в настройках бэкенда, а продаётся ли тариф по ссылке и
   опубликован ли курс, знает только он — фронт этого не знает и знать не должен.
   Свой запасной адрес — регистрация: она на том же origin, и до неё дойдём даже
   когда бэкенд молчит.

   Ошибку глотаем: это трекинг. Он не имеет права мешать человеку дойти до
   страницы — ни сообщением, ни лишней секундой ожидания. */

import React from 'react';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { RR } from '../../lib/router.jsx';
// не весь клиент раздела (lib/api/utm.js): этот экран — в главном чанке
import { trackUtmHit } from '../../lib/api/utmHit.js';

const { useEffect } = React;
const { useNavigate, useParams } = RR;

// Потолок ожидания трекинга. Ответ обычно приходит за десятки миллисекунд;
// если бэкенд задумался или лёг — уводим человека дальше, не дожидаясь его.
const MAX_WAIT_MS = 2500;

/* Назначения со слагом. Сегмент назначения в таких адресах статический
   (`/r/<код>/buy/<слаг>`, `/r/<код>/course/<слаг>`) и в параметры роута не
   попадает, поэтому его передаёт элемент роута пропсом `dest`. Белый список —
   чтобы опечатка в таблице роутов не отправила бэкенду назначение, которого он не
   знает: неизвестное — прежнее 'buy'. */
const SLUG_DESTS = ['buy', 'course'];

/* Что засчитать по параметрам адреса. Слаг есть у ссылок «на покупку» и «на
   страницу курса»; назначение со слагом — `dest` (по умолчанию 'buy': у роута
   тарифа пропса нет). Без слага — назначение из адреса, как было. */
export function hitOf({ code, to, slug, dest } = {}) {
  if (slug) return { code, to: SLUG_DESTS.includes(dest) ? dest : 'buy', slug };
  return { code, to: to || undefined, slug: undefined };
}

/* Параметры перехода: параметры адреса плюс назначение из пропса роута — чистой
   функцией, которую зовёт эффект экрана (и её же проходит тест). Потерять `dest` по
   дороге значило бы молча засчитать ссылку на курс как ссылку на тариф (hitOf по
   умолчанию — 'buy'): переход лёг бы не в то назначение, а бэкенд, не найдя такого
   тарифа, увёл бы человека на регистрацию вместо страницы курса. Из параметров роута
   берём только свои ключи. */
export function captureParamsOf(params, dest) {
  const { code, to, slug } = params || {};
  return { code, to, slug, dest };
}

/* Куда вести по адресу из ответа. Внешний адрес (лендинг) — только сменой
   location: SPA-навигация уводит внутри своего origin и на чужой домен не
   уходит вовсе. Свой адрес (регистрация, страница оплаты `/buy/<слаг>?utm_…`)
   отдаём роутеру вместе со строкой запроса — в ней метка кампании — и якорем.
   «Свой» — по разобранному URL (схема, хост и порт целиком), а не по началу
   строки: `https://app.restorium.su.evil.com/x` начинается с нашего origin, но
   это чужой хост, и роутер получил бы «.evil.com/x». Не http(s) (`javascript:`,
   `data:`) и неразборчивое в location не отдаём никогда — запасная регистрация. */
export function targetOf(url, origin) {
  const raw = String(url || '');
  if (!raw) return { spa: '/signup' };
  let parsed;
  try {
    parsed = new URL(raw, origin);
  } catch (e) {
    return { spa: '/signup' };
  }
  if (parsed.origin === origin) return { spa: parsed.pathname + parsed.search + parsed.hash };
  if (parsed.protocol === 'https:' || parsed.protocol === 'http:') return { external: parsed.href };
  return { spa: '/signup' };
}

/* Увод по адресу из ответа: чужой — `location.replace`, свой — роутером.
   replace в обоих случаях — чтобы «назад» не возвращал на этот же роут и не
   запускал переход по кругу. `loc` подставляет тест; по умолчанию — настоящий. */
export function goTo(url, navigate, loc = window.location) {
  const target = targetOf(url, loc.origin);
  if (target.external) loc.replace(target.external);
  else navigate(target.spa, { replace: true });
}

/* Переход целиком, как его запускает экран: трекинг → увод. Отдельной функцией,
   чтобы тест прошёл ровно ту связку, что живёт в эффекте. */
export function captureAndGo(params, navigate, opts = {}) {
  const { loc, ...rest } = opts;
  return startCapture(params, { ...rest, go: (url) => goTo(url, navigate, loc) });
}

/* Сам переход: трекинг с потолком ожидания и ровно одним уводом. Вынесен из
   эффекта, чтобы тест прошёл его целиком — с настоящим клиентом API.
   Адрес лежит в `data` конверта apiFetch ({ok, status, data}). Прежнее чтение
   `res.url` всегда давало пусто, и фолбэк молча уводил на регистрацию любую
   ссылку — и на лендинг, и на демо-стенд. */
export function startCapture(params, { go, track = trackUtmHit, wait = MAX_WAIT_MS }) {
  let done = false;
  const finish = (url) => {
    if (done) return;
    done = true;
    go(url);
  };
  const timer = setTimeout(() => finish(''), wait);
  const hit = hitOf(params);

  if (!hit.code) {
    finish('');
  } else {
    // apiFetch не бросает по HTTP-кодам, но отказ самого fetch (офлайн,
    // блокировщик) — обычное исключение: без catch экран завис бы на спиннере
    // до таймаута.
    Promise.resolve()
      .then(() => track(hit.code, hit.to, hit.slug))
      .catch(() => null)
      .then((res) => finish((res && res.data && res.data.url) || ''));
  }

  // Уборка гасит и таймер, и сам увод: ответ, пришедший после ухода с экрана
  // (назад, смена адреса), не имеет права утащить человека со страницы, где он
  // уже находится.
  return () => {
    done = true;
    clearTimeout(timer);
  };
}

/* `dest` — назначение ссылки со слагом из таблицы роутов ('course' у
   /r/:code/course/:slug; у тарифа не передаётся — см. hitOf). */
function UtmCapture({ dest }) {
  const { code, to, slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => captureAndGo(captureParamsOf({ code, to, slug }, dest), navigate),
    [code, to, slug, dest, navigate]);

  return (
    <div className="utm-capture" role="status" aria-live="polite">
      <Spinner size="lg" />
      <p className="t-body text-dim">Открываем страницу…</p>
    </div>
  );
}

export { UtmCapture };
